import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Users, Trophy, TrendingUp, Calendar, Target } from 'lucide-react';
import TeamPerformanceChart from '@/components/analytics/TeamPerformanceChart';
import PlayerAnalytics from '@/components/analytics/PlayerAnalytics';
import CompetitiveAnalysis from '@/components/analytics/CompetitiveAnalysis';
import { analyticsService } from '@/services/analyticsService';

const AnalyticsDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState<any>(null);
  const [topPerformers, setTopPerformers] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      const [stats, performers] = await Promise.all([
        analyticsService.getOverviewStatistics(),
        analyticsService.getTopPerformers()
      ]);
      setOverviewData(stats);
      setTopPerformers(performers);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic overview statistics from real data
  const overviewStats = overviewData ? [
    { 
      title: 'Total Games', 
      value: overviewData.totalGames.toString(), 
      icon: Calendar, 
      trend: overviewData.totalGamesThisMonth > 0 ? `+${overviewData.totalGamesThisMonth} this month` : 'No games this month', 
      color: 'text-blue-500' 
    },
    { 
      title: 'Win Rate', 
      value: `${overviewData.winRate}%`, 
      icon: Trophy, 
      trend: overviewData.winRateChange > 0 ? `+${overviewData.winRateChange}% recent` : overviewData.winRateChange < 0 ? `${overviewData.winRateChange}% recent` : 'No change', 
      color: 'text-green-500' 
    },
    { 
      title: 'Goals Scored', 
      value: overviewData.goalsScored.toString(), 
      icon: Target, 
      trend: overviewData.goalsPerGame > 0 ? `${overviewData.goalsPerGame} per game` : 'No goals recorded', 
      color: 'text-orange-500' 
    },
    { 
      title: 'Active Players', 
      value: overviewData.activePlayers.toString(), 
      icon: Users, 
      trend: overviewData.newPlayersThisMonth > 0 ? `${overviewData.newPlayersThisMonth} new this month` : 'No new players', 
      color: 'text-purple-500' 
    },
  ] : [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 py-8" style={{paddingTop: '100px'}}>
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Analytics Dashboard</h1>
              <p className="text-muted-foreground">
                Comprehensive performance insights and team analytics
              </p>
            </div>
            <Badge variant="secondary" className="text-sm">
              <TrendingUp className="h-4 w-4 mr-1" />
              Live Data
            </Badge>
          </div>

          {/* Analytics Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="team" className="flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Team Performance
              </TabsTrigger>
              <TabsTrigger value="players" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Player Analytics
              </TabsTrigger>
              <TabsTrigger value="competitive" className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Competitive Analysis
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Overview Statistics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {loading ? (
                  // Loading skeleton
                  Array.from({ length: 4 }).map((_, index) => (
                    <Card key={index}>
                      <CardContent className="p-6">
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-muted rounded w-2/3"></div>
                          <div className="h-8 bg-muted rounded w-1/2"></div>
                          <div className="h-3 bg-muted rounded w-3/4"></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  overviewStats.map((stat, index) => {
                    const IconComponent = stat.icon;
                    return (
                      <Card key={index}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">{stat.title}</p>
                              <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                              <p className="text-xs text-muted-foreground mt-1">{stat.trend}</p>
                            </div>
                            <IconComponent className={`h-8 w-8 ${stat.color}`} />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>

              {/* Quick Overview Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <TeamPerformanceChart />
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Quick Player Insights
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="animate-pulse space-y-4">
                        {Array.from({ length: 4 }).map((_, index) => (
                          <div key={index} className="flex justify-between items-center">
                            <div className="h-4 bg-muted rounded w-1/3"></div>
                            <div className="h-6 bg-muted rounded w-1/2"></div>
                          </div>
                        ))}
                      </div>
                    ) : topPerformers ? (
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Top Scorer</span>
                          <Badge variant="default">{topPerformers.topScorer.name} - {topPerformers.topScorer.points} pts</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Most Assists</span>
                          <Badge variant="secondary">{topPerformers.mostAssists.name} - {topPerformers.mostAssists.assists} assists</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Most Consistent</span>
                          <Badge variant="outline">{topPerformers.mostConsistent.name} - {topPerformers.mostConsistent.avg} avg</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Rising Star</span>
                          <Badge variant="default">{topPerformers.risingStar.name} - {topPerformers.risingStar.change > 0 ? '+' : ''}{topPerformers.risingStar.change}%</Badge>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No performance data available</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="team" className="space-y-6">
              <TeamPerformanceChart />
            </TabsContent>

            <TabsContent value="players" className="space-y-6">
              <PlayerAnalytics />
            </TabsContent>

            <TabsContent value="competitive" className="space-y-6">
              <CompetitiveAnalysis />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AnalyticsDashboard;